import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import BankingService from 'services/Banking'
import OnlinePaymentService from 'services/OnlinePayment'
import { logApiError } from 'services/apiError'
import { BANKING_ENABLED } from 'services/features'
import CheckBox from 'components/CheckBox'
import Tooltip from 'components/Tooltip'
import NumberInput from 'components/Inputs/NumberInput'
import MainButton from 'components/Buttons/MainButton'
import AlertDialog from 'components/AlertDialog'
import onlinePaymentConfigValidator from 'validators/onlinePaymentConfigValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'
import { MdPix } from 'react-icons/md'
import { FaCreditCard } from 'react-icons/fa'

import {
  Container,
  Form,
  Column,
  CheckRow,
  SubTitle,
  InfoText,
  Label,
  LinkText,
  WarningText
} from '../../styles'

/*
  Sinal do agendamento online: o salão liga a cobrança adiantada e escolhe quanto do preço
  do serviço o cliente paga na hora de marcar.

  O dinheiro cai na subconta do salão no Cutilei Pay, então sem conta ativa não há para
  onde mandá-lo — é por isso que esta aba tem um estado vazio em vez de um formulário que
  a API recusaria ao salvar.
*/
export default function EditOnlinePayment () {
  const [loading, setLoading] = useState (true)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [accountStatus, setAccountStatus] = useState (null)
  const [config, setConfig] = useState ({
    enabled: false,
    depositPercentage: 30,
    methods: ['pix']
  })

  const hasEditPermission = AuthService.hasPermission ('business_config.agenda', 'edit')
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${AuthService.getToken ()}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  const getData = async () => {
    try {
      const { data } = await cutileiApi.get (
        `/businesses/${businessId}/online_payment_config`, requestConfig
      )

      setAccountStatus (data.accountStatus)
      setConfig ({
        enabled: data.enabled ?? false,
        depositPercentage: data.depositPercentage ?? 30,
        methods: data.methods ?? ['pix']
      })
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  const toggleMethod = (methods, method) => (
    methods.includes (method)
      ? methods.filter (item => item !== method)
      : [...methods, method]
  )

  if (loading) return <Loading style={{marginTop: 30}}/>

  if (accountStatus !== 'active') return (
    <Container style={{gap: 10, marginTop: 30}}>
      <SubTitle style={{marginBottom: 0}}>
        Ative o Cutilei Pay para receber pagamentos online
      </SubTitle>
      <InfoText>
        {'O sinal do agendamento cai direto na sua conta Cutilei Pay. '
        + `Situação da sua conta hoje: ${BankingService.getStatusLabel (accountStatus)}.`}
      </InfoText>
      {/*
        A área bancária ainda não foi lançada: em produção a rota nem existe, e o link
        cairia no curinga que devolve para a home. Ver `services/features.js`.
      */}
      {BANKING_ENABLED && (
        <LinkText to='/business/banking'>Ir para o Cutilei Pay</LinkText>
      )}
    </Container>
  )

  return (
    <Formik
      initialValues={config}
      validationSchema={onlinePaymentConfigValidator}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          setSubmitting (true)

          await cutileiApi.post (
            `/businesses/${businessId}/online_payment_config`, values, requestConfig
          )

          resetForm ({ values })
          setSubmitting (false)
          toggleOkDialog ()
        } catch (error) {
          setSubmitting (false)
          logApiError (error)
        }
      }}
    >
      {({
        values,
        errors,
        dirty,
        isSubmitting,
        setFieldValue,
        handleSubmit
      }) => (
        <Container>
          <Form width={640}>
            <SubTitle>Pagamento online</SubTitle>
            <CheckRow>
              <CheckBox
                id='online_payment_enabled'
                value={values.enabled}
                disabled={!hasEditPermission}
                onValueChange={value => setFieldValue ('enabled', value)}
              />
              <Label htmlFor='online_payment_enabled'>
                Oferecer pagamento adiantado no meu link de agendamento
              </Label>
              <Tooltip
                id='online_payment_enabled'
                labelStyle={{width: 320}}
                message={
                  'Ao marcar um horário pelo seu link, o cliente pode adiantar parte do '
                  + 'valor do serviço. O pagamento é opcional: quem não quiser pagar '
                  + 'continua conseguindo agendar, e paga tudo no salão.'
                }
              />
            </CheckRow>
            {values.enabled && (
              <>
                <Column style={{gap: 5, marginTop: 10}}>
                  <Label htmlFor='depositPercentage'>
                    Porcentagem do valor a ser cobrado
                  </Label>
                  <NumberInput
                    id='depositPercentage'
                    name='depositPercentage'
                    value={values.depositPercentage}
                    minValue={1}
                    maxValue={100}
                    step={5}
                    disabled={!hasEditPermission}
                    onChange={setFieldValue}
                    style={{width: 140}}
                  />
                  {errors.depositPercentage && (
                    <WarningText>{errors.depositPercentage}</WarningText>
                  )}
                  <InfoText style={{color: '#777777'}}>
                    {'Num serviço de R$ 100,00, o cliente adianta '
                    + `R$ ${values.depositPercentage},00 e paga o restante no salão.`}
                  </InfoText>
                </Column>
                <SubTitle>Formas de pagamento aceitas</SubTitle>
                <Column style={{gap: 0}}>
                  <CheckRow>
                    <CheckBox
                      id='online_payment_pix'
                      value={values.methods.includes ('pix')}
                      disabled={!hasEditPermission}
                      onValueChange={() => (
                        setFieldValue ('methods', toggleMethod (values.methods, 'pix'))
                      )}
                    />
                    <Label htmlFor='online_payment_pix'>
                      {OnlinePaymentService.getMethodLabel ('pix')}
                    </Label>
                    <MdPix size={18} color='#32BCAD' style={{marginLeft: 3}}/>
                  </CheckRow>
                  <CheckRow>
                    <CheckBox
                      id='online_payment_credit_card'
                      value={values.methods.includes ('credit_card')}
                      disabled={!hasEditPermission}
                      onValueChange={() => (
                        setFieldValue ('methods', toggleMethod (values.methods, 'credit_card'))
                      )}
                    />
                    <Label htmlFor='online_payment_credit_card'>
                      {OnlinePaymentService.getMethodLabel ('credit_card')}
                    </Label>
                    <FaCreditCard size={16} color='#252525' style={{marginLeft: 3}}/>
                  </CheckRow>
                  {errors.methods && <WarningText>{errors.methods}</WarningText>}
                </Column>
              </>
            )}
            {dirty && (
              <MainButton
                label='Salvar alterações'
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                width={160}
                style={{marginTop: 5}}
              />
            )}
            <AlertDialog
              visible={showOkDialog}
              title='Sucesso'
              message='Pagamento online atualizado!'
              confirmText='Ok'
              onConfirm={toggleOkDialog}
              onClose={toggleOkDialog}
            />
          </Form>
        </Container>
      )}
    </Formik>
  )
}
